import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

interface ProductFieldValue {
    id?: number;
    product_field_definition_id: number;
    value: string;
}

interface OptionValue {
    id: number;
    name: string;
    presentation: string;
}

interface OptionType {
    id: number | string;
    name: string;
    presentation: string;
    option_values: OptionValue[];
}

interface SelectedOptionType {
    option_type_id: string | number;
    values: OptionValue[];
    name: string;
}

interface Variant {
    id: number | null;
    sku: string;
    mrp: string | number;
    stock_quantity: number;
    active: boolean;
    options: any[];
    variant_option_values_attributes?: { option_value_id: number }[];
    _destroy?: boolean;
}

interface ProductFieldDefinition {
    id: number;
    field_key: string;
    field_label: string;
    field_type: string;
    required: boolean;
    choices?: string[];
    group_key?: string;
    options?: any;
}

interface Category {
    id: number;
    name: string;
}

interface FormData {
    name: string;
    sku: string;
    category_id: string | number;
    product_type: string;
    description: string;
    purchase_price: string | number;
    mrp: string | number;
    stock_quantity: string | number;
    low_stock_threshold: string | number;
    unit: string;
    tax_rate_id: string | number;
    tax_inclusive: boolean;
    hsn_code: string;
    serialized: boolean;
    track_batches: boolean;
    tracking_types: string[];
    active: boolean;
    show_in_store: boolean;
    generate_qr_code: boolean;
    batch_number: string;
    has_variants: boolean;
    discontinued: boolean;
    product_field_values_attributes: ProductFieldValue[];
}

function ProductForm() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const isEdit = !!id;

    const [activeTab, setActiveTab] = useState<'general' | 'pricing' | 'custom' | 'variants'>('general');

    const currentFirm = (window as any).CURRENT_FIRM;
    const defaultProductType = currentFirm?.industry || 'normal';

    const [formData, setFormData] = useState<FormData>({
        name: '',
        sku: '',
        category_id: '',
        product_type: isEdit ? 'normal' : defaultProductType,
        description: '',
        purchase_price: '',
        mrp: '',
        stock_quantity: '',
        low_stock_threshold: '',
        unit: 'pcs',
        tax_rate_id: '',
        tax_inclusive: false,
        hsn_code: '',
        serialized: false,
        track_batches: false,
        tracking_types: [],
        active: true,
        show_in_store: false,
        generate_qr_code: false,
        batch_number: '',
        has_variants: false,
        discontinued: false,
        product_field_values_attributes: []
    });

    const [imageUrls, setImageUrls] = useState<{ id: number; url: string }[]>([]);
    const [uploadingImage, setUploadingImage] = useState(false);

    const [fieldDefinitions, setFieldDefinitions] = useState<ProductFieldDefinition[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [optionTypes, setOptionTypes] = useState<OptionType[]>([]);
    const [selectedOptionTypes, setSelectedOptionTypes] = useState<SelectedOptionType[]>([]);
    const [generatedVariants, setGeneratedVariants] = useState<Variant[]>([]);
    const [skuError, setSkuError] = useState<string | null>(null);
    const [checkingSku, setCheckingSku] = useState(false);
    const [taxRates, setTaxRates] = useState<any[]>([]);

    useEffect(() => {
        loadCategories();
        loadOptionTypes();
        loadTaxRates();
        if (isEdit) {
            loadProduct();
        }
    }, [id]);

    useEffect(() => {
        loadFieldDefinitions(formData.product_type);
    }, [formData.product_type]);

    const loadFieldDefinitions = async (industryFilter: string) => {
        try {
            const url = `/settings/product_fields${industryFilter && industryFilter !== 'normal' ? `?industry=${industryFilter}` : ''}`;
            const response = await api.get(url);
            setFieldDefinitions(response.data || []);
            
            // Re-load categories and option types to catch any newly seeded data
            if (industryFilter && industryFilter !== 'normal') {
                loadCategories();
                loadOptionTypes();
            }
        } catch (err: any) {
            console.error('Failed to load field definitions', err);
        }
    };

    const loadOptionTypes = async () => {
        try {
            const response = await api.get('/option_types');
            setOptionTypes(response.data?.option_types || response.data?.data || []);
        } catch (err: any) {
            console.error('Failed to load option types', err);
        }
    };

    const loadTaxRates = async () => {
        try {
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || response.data?.data || []);
        } catch (err: any) {
            console.error('Failed to load tax rates', err);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await api.get('/categories');
            const categoriesData = Array.isArray(response.data) ? response.data : (response.data?.categories || response.data?.data || []);
            setCategories(categoriesData);
            
            // Auto-select category if there's only one and none is selected yet
            if (!isEdit && categoriesData.length === 1 && !formData.category_id) {
                setFormData(prev => ({ ...prev, category_id: categoriesData[0].id }));
            }
        } catch (err: any) {
            console.error('Failed to load categories', err);
        }
    };

    const loadProduct = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/products/${id}`);
            const product = response.data?.product || response.data;
            if (product) {
                setFormData((prev: FormData) => ({
                    ...prev,
                    name: product.name || '',
                    sku: product.sku || '',
                    category_id: product.category_id || '',
                    product_type: product.product_type || 'normal',
                    description: product.description || '',
                    purchase_price: product.purchase_price || product.mrp || '',
                    mrp: product.mrp || product.purchase_price || '',
                    stock_quantity: product.stock_quantity || '',
                    low_stock_threshold: product.low_stock_threshold || '',
                    unit: product.unit || 'pcs',
                    tax_rate_id: product.tax_rate_id || '',
                    tax_inclusive: product.tax_inclusive || false,
                    hsn_code: product.hsn_code || '',
                    serialized: product.serialized || false,
                    track_batches: product.track_batches || false,
                    tracking_types: product.tracking_types || [],
                    active: product.active !== undefined ? product.active : true,
                    show_in_store: product.show_in_store || false,
                    generate_qr_code: product.generate_qr_code || false,
                    batch_number: product.batch_number || '',
                    has_variants: product.variants && product.variants.length > 0,
                    discontinued: product.discontinued || false,
                    product_field_values_attributes: product.product_field_values || []
                }));

                setImageUrls(product.image_urls || []);

                if (product.variants && product.variants.length > 0) {
                    setGeneratedVariants(product.variants.map((v: any) => ({
                        ...v,
                        mrp: v.mrp || product.mrp
                    })));

                    // Reconstruct Selected Option Types from variants
                    const reconstructedOptions: SelectedOptionType[] = [];
                    product.variants.forEach((v: any) => {
                        v.options.forEach((opt: any) => {
                            let group = reconstructedOptions.find(ro => String(ro.option_type_id) === String(opt.option_type_id));
                            if (!group) {
                                group = {
                                    option_type_id: opt.option_type_id,
                                    name: opt.type_presentation,
                                    values: []
                                };
                                reconstructedOptions.push(group);
                            }
                            if (!group.values.find(ov => ov.id === opt.id)) {
                                group.values.push({
                                    id: opt.id,
                                    name: opt.name,
                                    presentation: opt.presentation
                                });
                            }
                        });
                    });
                    setSelectedOptionTypes(reconstructedOptions);
                }
            } else {
                setError('Product not found');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load product');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type, checked } = e.target as HTMLInputElement;
        const finalValue = type === 'checkbox' ? checked : value;
        if (name === 'mrp') {
            setFormData((prev: any) => ({
                ...prev,
                purchase_price: value,
                mrp: value
            }));
        } else {
            setFormData((prev: any) => ({
                ...prev,
                [name]: finalValue
            }));
        }

        if (name === 'sku' && value) {
            checkSkuDuplicate(value);
        }
    };

    const handleTrackingTypeChange = (index: number, value: string) => {
        setFormData(prev => {
            const types = [...prev.tracking_types];
            types[index] = value;
            return { ...prev, tracking_types: types };
        });
    };

    const addTrackingType = () => {
        setFormData(prev => ({
            ...prev,
            tracking_types: [...prev.tracking_types, '']
        }));
    };

    const removeTrackingType = (index: number) => {
        setFormData(prev => {
            const types = [...prev.tracking_types];
            types.splice(index, 1);
            return { ...prev, tracking_types: types };
        });
    };

    const handleCustomFieldChange = (definitionId: number, value: any) => {
        setFormData((prev: any) => {
            const currentFields = [...prev.product_field_values_attributes];
            const existingIndex = currentFields.findIndex(v => v.product_field_definition_id === definitionId);

            let updatedFields;
            if (existingIndex !== -1) {
                updatedFields = currentFields.map((v, index) =>
                    index === existingIndex ? { ...v, value: value.toString() } : v
                );
            } else {
                updatedFields = [
                    ...currentFields,
                    { product_field_definition_id: definitionId, value: value.toString() }
                ];
            }
            return {
                ...prev,
                product_field_values_attributes: updatedFields
            };
        });
    };

    const checkSkuDuplicate = async (sku: string) => {
        if (!sku || sku.length < 2) {
            setSkuError(null);
            return;
        }

        setCheckingSku(true);
        try {
            const response = await api.get('/products', { q: sku });
            const products = response.data?.products || response.data || [];

            const duplicate = products.find((p: any) =>
                p.sku?.toLowerCase() === sku.toLowerCase() &&
                (!isEdit || p.id !== parseInt(id || '0'))
            );

            if (duplicate) {
                setSkuError(`SKU "${sku}" is already used by "${duplicate.name}"`);
            } else {
                setSkuError(null);
            }
        } catch (err) {
            console.error('SKU check failed', err);
        } finally {
            setCheckingSku(false);
        }
    };

    const addOptionType = () => {
        setSelectedOptionTypes([...selectedOptionTypes, { option_type_id: '', values: [], name: '' }]);
    };

    const removeOptionType = (index: number) => {
        const newTypes = [...selectedOptionTypes];
        newTypes.splice(index, 1);
        setSelectedOptionTypes(newTypes);
    };

    const handleOptionTypeChange = (index: number, typeId: string | number) => {
        const type = optionTypes.find(t => String(t.id) === String(typeId));
        if (!type) return;
        const newTypes = [...selectedOptionTypes];
        newTypes[index] = { option_type_id: typeId, values: [], name: type.presentation };
        setSelectedOptionTypes(newTypes);
    };

    const handleOptionValueToggle = (index: number, valueObj: OptionValue) => {
        const newTypes = [...selectedOptionTypes];
        const currentValues = newTypes[index].values;
        const exists = currentValues.find(v => v.id === valueObj.id);

        if (exists) {
            newTypes[index].values = currentValues.filter(v => v.id !== valueObj.id);
        } else {
            newTypes[index].values = [...currentValues, valueObj];
        }
        setSelectedOptionTypes(newTypes);
    };

    const createOptionType = async () => {
        const name = prompt("Enter new option type name (e.g., Size, Color):");
        if (!name) return;
        try {
            const res = await api.post('/option_types', { option_type: { name, presentation: name } });
            if (res.data?.success || res.success) {
                const newType = res.data?.option_type || res.option_type;
                setOptionTypes([...optionTypes, newType]);
            }
        } catch (e: any) {
            console.error(e);
            toast.error("Failed to create option type");
        }
    };

    const createOptionValue = async (typeId: string | number, index: number) => {
        const name = prompt("Enter new option value (e.g., Small, Blue):");
        if (!name) return;
        try {
            const res = await api.put(`/option_types/${typeId}`, {
                option_type: {
                    option_values_attributes: [{ name, presentation: name }]
                }
            });
            if (res.data?.success || res.success) {
                const updatedType = res.data?.option_type || res.option_type;
                setOptionTypes((prev) => prev.map(t => String(t.id) === String(typeId) ? updatedType : t));

                const newValue = updatedType.option_values.find((v: OptionValue) => v.name === name);
                if (newValue) {
                    handleOptionValueToggle(index, newValue);
                }
            }
        } catch (e: any) {
            console.error(e);
            toast.error("Failed to create option value");
        }
    };

    const handleGenerateVariants = () => {
        if (selectedOptionTypes.length === 0 || selectedOptionTypes.some(t => t.values.length === 0)) {
            toast.warning("Please select option values for all types.");
            return;
        }

        const combinations = cartesian(...selectedOptionTypes.map(t => t.values));

        const newVariants: Variant[] = combinations.map((combo: any) => {
            const options = Array.isArray(combo) ? combo : [combo];
            const sig = options.map((o: any) => o.id).sort().join('-');

            const existing = generatedVariants.find(v => {
                const vSig = v.options.map((o: any) => o.id).sort().join('-');
                return vSig === sig && !v._destroy;
            });

            if (existing) return existing;

            return {
                id: null,
                sku: `${formData.sku}-${options.map((o: any) => o.presentation).join('-')}`,
                mrp: formData.mrp, // Defaulting price to MRP
                stock_quantity: 0,
                active: true,
                options: options,
                variant_option_values_attributes: options.map((o: any) => ({ option_value_id: o.id }))
            };
        });

        const validSignatures = newVariants.map(v => v.options.map((o: any) => o.id).sort().join('-'));

        const mergedVariants = [
            ...generatedVariants.map(v => {
                const vSig = v.options.map((o: any) => o.id).sort().join('-');
                if (!validSignatures.includes(vSig) && v.id) {
                    return { ...v, _destroy: true };
                } else if (!validSignatures.includes(vSig) && !v.id) {
                    return null;
                }
                return v;
            }).filter((v): v is Variant => v !== null),
            ...newVariants.filter(nv => !generatedVariants.some(gv => {
                const gvSig = gv.options.map((o: any) => o.id).sort().join('-');
                const nvSig = nv.options.map((o: any) => o.id).sort().join('-');
                return gvSig === nvSig;
            }))
        ];

        setGeneratedVariants(mergedVariants);
    };

    const cartesian = (...a: any[][]) => a.reduce((acc: any[], b: any[]) => acc.flatMap(d => b.map(e => [d, e].flat())));

    const updateVariant = (index: number, field: string, value: any) => {
        const newVars = [...generatedVariants];
        newVars[index] = { ...newVars[index], [field]: value };
        setGeneratedVariants(newVars);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);

            const payload = {
                ...formData,
                product_variants_attributes: formData.has_variants ? generatedVariants.map(v => ({
                    id: v.id,
                    sku: v.sku,
                    mrp: v.mrp,
                    stock_quantity: v.stock_quantity,
                    active: v.active,
                    _destroy: v._destroy,
                    // Only send option values attributes for new variants to prevent duplicates
                    ...(v.id ? {} : { variant_option_values_attributes: v.variant_option_values_attributes || v.options.map((o: any) => ({ option_value_id: o.id })) })
                })) : []
            };

            const response = isEdit
                ? await api.put(`/products/${id}`, { product: payload })
                : await api.post('/products', { product: payload });

            if (response.success) {
                const productData = response.data?.product || response.data;
                if (productData?.id) {
                    toast.success(`Product ${isEdit ? 'updated' : 'created'} successfully`);
                    navigate(`/products/${productData.id}`);
                } else {
                    const msg = 'Product saved but no ID returned';
                    setError(msg);
                    toast.error(msg);
                }
            } else {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || 'Failed to save product'];
                
                const errorMsg = backendErrors.join(', ');
                setError(errorMsg);
                toast.error(backendErrors[0]);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err: any) {
            const msg = err.message || 'An unexpected error occurred';
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !id) return;

        if (imageUrls.length >= 4) {
            toast.error('Maximum 4 images allowed');
            return;
        }

        const uploadData = new FormData();
        uploadData.append('image', file);

        try {
            setUploadingImage(true);
            const response = await api.post(`/products/${id}/upload_image`, uploadData);
            if (response.success) {
                toast.success('Image uploaded successfully');
                // Reload product or update state
                loadProduct();
            } else {
                toast.error(response.message || 'Failed to upload image');
            }
        } catch (err: any) {
            toast.error('Upload failed');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleImageDelete = async (imageId: number) => {
        if (!confirm('Are you sure you want to delete this image?')) return;

        try {
            const response = await api.delete(`/products/${id}/delete_image?image_id=${imageId}`);
            if (response.success) {
                toast.success('Image deleted');
                setImageUrls(prev => prev.filter(img => img.id !== imageId));
            } else {
                toast.error(response.message || 'Failed to delete image');
            }
        } catch (err: any) {
            toast.error('Delete failed');
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mb-4"></div>
            <p className="text-slate-500 font-medium">Loading product details...</p>
        </div>
    );

    const tabs = [
        {
            id: 'general', label: 'General Info', icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        },
        {
            id: 'pricing', label: 'Pricing & Stock', icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        },
        {
            id: 'custom', label: 'Quality & Industry', icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            )
        },
        {
            id: 'variants', label: 'Variants', icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <Link to="/products" className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-2xl shadow-sm transition-all border border-transparent hover:border-slate-200">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </Link>
                        <div>
                            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">{isEdit ? 'Refine Product' : 'Add New Product'}</h1>
                            <p className="text-slate-500 font-medium">Configure details, pricing, and variants</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                        <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-xl flex items-center gap-3 shadow-sm">
                            <svg className="w-5 h-5 text-rose-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <p className="text-sm text-rose-800 font-bold">{error}</p>
                        </div>
                    )}

                    {/* Tabs Navigation */}
                    <div className="flex p-1.5 bg-slate-200/50 backdrop-blur-sm rounded-2xl border border-slate-200 mb-8 overflow-x-auto">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-bold transition-all shrink-0 ${activeTab === tab.id
                                    ? 'bg-white text-indigo-600 shadow-xl shadow-indigo-100 border border-slate-200'
                                    : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                                    }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="bg-white rounded-lg shadow-2xl shadow-slate-200 border border-slate-100 overflow-hidden">
                        <div className="p-10">
                            {/* General Tab */}
                            {activeTab === 'general' && (
                                <div className="space-y-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                    {/* Media Section - Marked as Disabled */}
                                    <div className="bg-slate-50 p-8 rounded-3xl border-2 border-slate-100 mb-8 relative">
                                        <div className="absolute top-4 right-4 z-10">
                                            <span className="px-3 py-1 bg-slate-200 text-slate-500 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-300 shadow-sm">
                                                Temporarily Disabled
                                            </span>
                                        </div>
                                        
                                        <div className="opacity-40 grayscale pointer-events-none">
                                            <div className="flex items-center justify-between mb-6">
                                                <div>
                                                    <h3 className="text-lg font-black text-slate-800 tracking-tight">Product Media</h3>
                                                    <p className="text-sm text-slate-500 font-medium">Add up to 4 high-quality product images</p>
                                                </div>
                                                {isEdit && (
                                                    <label className={`cursor-pointer px-6 py-3 bg-indigo-600 text-white rounded-xl text-sm font-black uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2`}>
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                                                        Upload
                                                    </label>
                                                )}
                                            </div>

                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                {imageUrls.map((img) => (
                                                    <div key={img.id} className="relative aspect-square rounded-2xl overflow-hidden border-2 border-slate-200 bg-white">
                                                        <img src={img.url} alt="Product" className="w-full h-full object-cover" />
                                                    </div>
                                                ))}
                                                {isEdit && imageUrls.length < 4 && (
                                                    <div className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-2xl bg-white">
                                                        <div className="p-3 rounded-full bg-slate-50 text-slate-400">
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                        </div>
                                                        <span className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Add Image</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-bold text-slate-700 mb-2">Product Name <span className="text-rose-500">*</span></label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-lg font-black text-slate-900"
                                                placeholder="e.g. Premium Wireless Headphones"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">Unique SKU</label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="sku"
                                                    value={formData.sku}
                                                    onChange={handleChange}
                                                    className={`w-full px-5 py-4 bg-slate-50 border-2 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all font-black tracking-wider text-slate-900 ${skuError ? 'border-rose-200 focus:border-rose-500' : 'border-slate-100 focus:border-indigo-500'}`}
                                                    placeholder="SKU-2024-001"
                                                />
                                                {checkingSku && <div className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-2 border-indigo-500 border-t-transparent"></div>}
                                            </div>
                                            {skuError && <p className="text-xs text-rose-600 mt-2 font-bold px-1">⚠ {skuError}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">Category</label>
                                            <select
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-black text-slate-900"
                                            >
                                                <option value="" className="text-slate-900">Uncategorized</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id} className="text-slate-900">{cat.name}</option>
                                                ))}
                                            </select>
                                        </div>                                         <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                                Industry Format
                                                {currentFirm?.industry && <span className="ml-2 text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full border border-indigo-100 uppercase tracking-tighter">Matches Firm: {currentFirm.industry}</span>}
                                            </label>
                                            <select
                                                name="product_type"
                                                value={formData.product_type}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-black text-slate-900"
                                            >
                                                <option value="normal" className="text-slate-900">Standard Product (Universal)</option>
                                                <option value="dairy" className="text-slate-900 font-bold">🥛 Milk & Dairy (Fat %, Volume)</option>
                                                <option value="electronics" className="text-slate-900">Electronics (Warranty, Brand)</option>
                                                <option value="vehicle" className="text-slate-900">Vehicle (Year, Mileage, Insurance)</option>
                                                <option value="grocery" className="text-slate-900">Grocery (Organic, Nutritional)</option>
                                                <option value="fashion" className="text-slate-900">Fashion (Material, Fit)</option>
                                                <option value="pharmacy" className="text-slate-900">Pharmacy / Healthcare</option>
                                                <option value="retail" className="text-slate-900">General Retail</option>
                                                <option value="rental" className="text-slate-900">Rental Services</option>
                                                <option value="shoes" className="text-slate-900">Shoes & Footwear</option>
                                                <option value="construction" className="text-slate-900">Construction Materials</option>
                                                <option value="installable" className="text-slate-900">Equipment Requiring Installation</option>
                                                <option value="other" className="text-slate-900">Other</option>
                                            </select>
                                            <p className="mt-2 text-[10px] text-slate-400 font-medium">Determines which custom fields (Quality & Industry tab) will be available.</p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">Unit of Measurement</label>
                                            <select
                                                name="unit"
                                                value={formData.unit}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-black text-slate-900"
                                            >
                                                <option value="pcs" className="text-slate-900">Pieces (pcs)</option>
                                                <option value="kg" className="text-slate-900">Kilograms (kg)</option>
                                                <option value="g" className="text-slate-900">Grams (g)</option>
                                                <option value="l" className="text-slate-900">Liters (l)</option>
                                                <option value="box" className="text-slate-900">Box</option>
                                                <option value="dozen" className="text-slate-900">Dozen</option>
                                            </select>
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-bold text-slate-700 mb-2">Description</label>
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows={4}
                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all resize-none font-medium"
                                                placeholder="Provide a detailed description of the product..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Pricing Tab */}
                            {activeTab === 'pricing' && (
                                <div className="space-y-12 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                    <div className="bg-indigo-50/30 p-8 rounded-lg border border-indigo-100/50">
                                        <h4 className="text-sm font-black text-indigo-900/40 uppercase tracking-[0.2em] mb-6">Financials</h4>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-bold text-slate-700 mb-2 font-mono uppercase tracking-widest">Inventory Valuation (MRP)</label>
                                            <div className="relative">
                                                <span className="absolute left-6 top-1/2 -translate-y-1/2 text-emerald-500 font-black">₹</span>
                                                <input
                                                    type="number"
                                                    name="mrp"
                                                    value={formData.mrp}
                                                    onChange={handleChange}
                                                    className="w-full pl-12 pr-8 py-6 bg-white border-2 border-emerald-100 rounded-3xl focus:ring-8 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all font-black text-2xl text-slate-900 group-hover:shadow-2xl group-hover:scale-[1.01]"
                                                    placeholder="0.00"
                                                />
                                            </div>
                                            <p className="mt-3 text-[10px] text-slate-400 font-bold uppercase tracking-widest">Calculates land value & stock equity</p>
                                        </div>
                                    </div>

                                    <div className="bg-amber-50/30 p-8 rounded-lg border border-amber-100/50">
                                        <h4 className="text-sm font-black text-amber-900/40 uppercase tracking-[0.2em] mb-6">Inventory Controls</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                            <div>
                                                <label className="block text-sm font-bold text-slate-700 mb-2">Available Stock</label>
                                                <input
                                                    type="number"
                                                    name="stock_quantity"
                                                    value={formData.stock_quantity}
                                                    onChange={handleChange}
                                                    className="w-full px-5 py-4 bg-white border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 outline-none transition-all font-mono text-lg"
                                                    placeholder="0"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-slate-700 mb-2 text-amber-600">Low Stock Warning</label>
                                                <input
                                                    type="number"
                                                    name="low_stock_threshold"
                                                    value={formData.low_stock_threshold}
                                                    onChange={handleChange}
                                                    className="w-full px-5 py-4 bg-white border-2 border-amber-200 rounded-2xl focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 outline-none transition-all font-mono text-lg"
                                                    placeholder="10"
                                                />
                                            </div>
                                            <div className="flex gap-4">
                                                <div className="flex-1">
                                                    <label className="block text-sm font-bold text-slate-700 mb-2">Tax Rate</label>
                                                    <select
                                                        name="tax_rate_id"
                                                        value={formData.tax_rate_id}
                                                        onChange={handleChange}
                                                        className="w-full px-5 py-4 bg-white border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-amber-500/10 focus:border-amber-500 outline-none transition-all font-bold"
                                                    >
                                                        <option value="">Select Tax Rate</option>
                                                        {taxRates.map(rate => (
                                                            <option key={rate.id} value={rate.id}>
                                                                {rate.name} ({rate.percentage}%)
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="flex items-end pb-4">
                                                    <label className="flex items-center gap-3 cursor-pointer select-none">
                                                        <div className="relative">
                                                            <input
                                                                type="checkbox"
                                                                name="tax_inclusive"
                                                                checked={formData.tax_inclusive}
                                                                onChange={handleChange}
                                                                className="sr-only"
                                                            />
                                                            <div className={`block w-14 h-8 rounded-full transition-colors ${formData.tax_inclusive ? 'bg-amber-500' : 'bg-slate-200'}`}></div>
                                                            <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${formData.tax_inclusive ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                        </div>
                                                        <span className="text-sm font-bold text-slate-700">Tax Inclusive</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-4 mt-8">
                                        <div className={`p-6 rounded-3xl border-2 transition-all cursor-pointer ${formData.discontinued ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-100 hover:border-slate-200'}`} onClick={() => setFormData(p => ({ ...p, discontinued: !p.discontinued }))}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className={`p-3 rounded-2xl ${formData.discontinued ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-slate-800">Discontinue Product</h4>
                                                        <p className="text-xs text-slate-500 font-medium">Prevent new orders but keep historical data</p>
                                                    </div>
                                                </div>
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${formData.discontinued ? 'bg-rose-600 border-rose-600' : 'border-slate-200'}`}>
                                                    {formData.discontinued && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-4">
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">HSN / SAC Code</label>
                                            <input
                                                type="text"
                                                name="hsn_code"
                                                value={formData.hsn_code}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-mono"
                                                placeholder="e.g. 8517"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-slate-700 mb-2">Batch Identification</label>
                                            <input
                                                type="text"
                                                name="batch_number"
                                                value={formData.batch_number}
                                                onChange={handleChange}
                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-mono"
                                                placeholder="e.g. SEP-2024-B1"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Custom & Tracking Tab */}
                            {activeTab === 'custom' && (
                                <div className="space-y-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className={`p-6 rounded-3xl border-2 transition-all cursor-pointer ${formData.serialized ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-100 hover:border-slate-200'}`} onClick={() => setFormData(p => ({ ...p, serialized: !p.serialized, tracking_types: !p.serialized && p.tracking_types.length === 0 ? ['Serial No'] : p.tracking_types }))}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className={`p-3 rounded-2xl ${formData.serialized ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-slate-800">Unit Serialization</h4>
                                                        <p className="text-xs text-slate-500 font-medium">Track unique IMEI/Serial for each piece</p>
                                                    </div>
                                                </div>
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${formData.serialized ? 'bg-indigo-600 border-indigo-600' : 'border-slate-200'}`}>
                                                    {formData.serialized && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`p-6 rounded-3xl border-2 transition-all cursor-pointer ${formData.track_batches ? 'bg-sky-50 border-sky-200' : 'bg-white border-slate-100 hover:border-slate-200'}`} onClick={() => setFormData(p => ({ ...p, track_batches: !p.track_batches }))}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className={`p-3 rounded-2xl ${formData.track_batches ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-slate-800">Batch Monitoring</h4>
                                                        <p className="text-xs text-slate-500 font-medium">Manage expiration, manufacturing & lots</p>
                                                    </div>
                                                </div>
                                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${formData.track_batches ? 'bg-sky-600 border-sky-600' : 'border-slate-200'}`}>
                                                    {formData.track_batches && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {formData.serialized && (
                                        <div className="space-y-6 p-8 bg-slate-50 rounded-lg border border-slate-100">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-bold text-slate-700">Tracking Types</h4>
                                                    <p className="text-xs text-slate-500 font-medium">Define exactly what identifiers to track per unit.</p>
                                                </div>
                                                <button type="button" onClick={addTrackingType} className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-colors">
                                                    + Add Identifier
                                                </button>
                                            </div>
                                            {formData.tracking_types.length === 0 && (
                                                <div className="p-4 bg-white rounded-lg border border-amber-200 text-sm text-amber-700 text-center">
                                                    At least one tracking type (e.g. IMEI) is required.
                                                </div>
                                            )}
                                            {formData.tracking_types.map((type, idx) => (
                                                <div key={idx} className="flex gap-4 items-end">
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-bold text-slate-500 mb-2">Identifier {idx + 1} Name</label>
                                                        <input
                                                            type="text"
                                                            value={type}
                                                            onChange={(e) => handleTrackingTypeChange(idx, e.target.value)}
                                                            className="w-full px-5 py-4 bg-white border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium"
                                                            placeholder="e.g. IMEI, Serial Number, MAC Address"
                                                            required
                                                        />
                                                    </div>
                                                    <button type="button" onClick={() => removeTrackingType(idx)} className="p-4 bg-white border-2 border-slate-100 text-rose-500 rounded-2xl hover:bg-rose-50 hover:border-rose-200 transition-colors shrink-0 group">
                                                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}


                                    <div className="space-y-8">
                                        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                                            <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">Tailored Industry Attributes</h3>
                                            <Link to="/settings/product-fields" className="text-xs font-bold text-indigo-600 hover:text-indigo-700 underline decoration-2 underline-offset-4">Manage Custom Fields</Link>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            {fieldDefinitions.map((field) => {
                                                const existingValue = formData.product_field_values_attributes.find(
                                                    v => v.product_field_definition_id === field.id
                                                )?.value || '';

                                                return (
                                                    <div key={field.id}>
                                                        <label className="block text-sm font-bold text-slate-700 mb-2">
                                                            {field.field_label} {field.required && <span className="text-rose-500">*</span>}
                                                        </label>
                                                        {field.field_type === 'dropdown' ? (
                                                            <select
                                                                value={existingValue}
                                                                onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                                                                required={field.required}
                                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium"
                                                            >
                                                                <option value="">Select {field.field_label}</option>
                                                                {(field.options?.values || field.choices || []).map((c: string) => <option key={c} value={c}>{c}</option>)}
                                                            </select>
                                                        ) : field.field_type === 'boolean' ? (
                                                            <div className="flex items-center gap-4 mt-1">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleCustomFieldChange(field.id, 'true')}
                                                                    className={`flex-1 py-4 rounded-2xl font-bold transition-all border-2 ${existingValue === 'true' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-slate-50 border-slate-100 text-slate-500 hover:border-slate-200'}`}
                                                                >Yes</button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleCustomFieldChange(field.id, 'false')}
                                                                    className={`flex-1 py-4 rounded-2xl font-bold transition-all border-2 ${existingValue === 'false' ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-slate-50 border-slate-100 text-slate-500 hover:border-slate-200'}`}
                                                                >No</button>
                                                            </div>
                                                        ) : (
                                                            <input
                                                                type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                                                                value={existingValue}
                                                                onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                                                                required={field.required}
                                                                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium"
                                                                placeholder={`Enter ${field.field_label}`}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Variants Tab */}
                            {activeTab === 'variants' && (
                                <div className="space-y-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
                                    <div className="flex items-center justify-between bg-slate-50 p-6 rounded-lg border border-slate-100">
                                        <div>
                                            <h4 className="font-bold text-slate-800">Support Product Variants</h4>
                                            <p className="text-xs text-slate-500 font-medium">Enable multiple sizes, colors, or materials</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setFormData(p => ({ ...p, has_variants: !p.has_variants }))}
                                            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-all focus:outline-none ${formData.has_variants ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                        >
                                            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-all ${formData.has_variants ? 'translate-x-7' : 'translate-x-1'}`} />
                                        </button>
                                    </div>

                                    {formData.has_variants && (
                                        <div className="space-y-10">
                                            <div className="space-y-6">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">Configurable Options</h3>
                                                    <button type="button" onClick={createOptionType} className="text-xs font-bold text-indigo-600 hover:text-indigo-700 underline decoration-2 underline-offset-4">Create New Option Type</button>
                                                </div>

                                                <div className="space-y-4">
                                                    {selectedOptionTypes.map((selected, idx) => (
                                                        <div key={idx} className="p-8 bg-slate-50 rounded-lg border border-slate-200 relative group">
                                                            <button type="button" onClick={() => removeOptionType(idx)} className="absolute -top-3 -right-3 w-8 h-8 bg-white text-slate-400 hover:text-rose-500 shadow-lg rounded-full flex items-center justify-center border border-slate-100 opacity-0 group-hover:opacity-100 transition-all">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                                                            </button>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                <div>
                                                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.1em] mb-3">Select Option Type</label>
                                                                    <select
                                                                        value={selected.option_type_id}
                                                                        onChange={(e) => handleOptionTypeChange(idx, e.target.value)}
                                                                        className="w-full px-5 py-4 bg-white border-2 border-slate-100 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700"
                                                                    >
                                                                        <option value="">Choose Type...</option>
                                                                        {optionTypes.map(ot => <option key={ot.id} value={ot.id}>{ot.presentation}</option>)}
                                                                    </select>
                                                                </div>
                                                                {selected.option_type_id && (
                                                                    <div>
                                                                        <div className="flex items-center justify-between mb-3">
                                                                            <label className="block text-xs font-black text-slate-400 uppercase tracking-[0.1em]">Select Values</label>
                                                                            <button type="button" onClick={() => createOptionValue(selected.option_type_id, idx)} className="text-[10px] uppercase font-black text-indigo-600 tracking-wider">Add New Value</button>
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {optionTypes.find(ot => String(ot.id) === String(selected.option_type_id))?.option_values.map(ov => (
                                                                                <button
                                                                                    key={ov.id}
                                                                                    type="button"
                                                                                    onClick={() => handleOptionValueToggle(idx, ov)}
                                                                                    className={`px-4 py-2 rounded-xl text-xs font-black border-2 transition-all ${selected.values.some(v => v.id === ov.id) ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100' : 'bg-white border-slate-100 text-slate-500 hover:border-slate-200'}`}
                                                                                >{ov.presentation}</button>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button type="button" onClick={addOptionType} className="w-full py-6 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 font-bold hover:border-indigo-200 hover:text-indigo-500 transition-all flex items-center justify-center gap-2 group">
                                                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                                                        Add Another Option Aspect
                                                    </button>
                                                </div>

                                                <div className="pt-6">
                                                    <button type="button" onClick={handleGenerateVariants} className="w-full py-5 bg-slate-900 text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] hover:bg-black transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-3 active:scale-[0.98]">
                                                        Generate Variants Matrix
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                                    </button>
                                                </div>
                                            </div>

                                            {generatedVariants.filter(v => !v._destroy).length > 0 && (
                                                <div className="space-y-6">
                                                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em]">Product Matrix</h3>
                                                    <div className="overflow-x-auto rounded-lg border border-slate-100">
                                                        <table className="min-w-full divide-y divide-slate-100">
                                                            <thead className="bg-slate-50">
                                                                <tr>
                                                                    <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Variant Specs</th>
                                                                    <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">SKU Identity</th>
                                                                    <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Pricing (₹)</th>
                                                                    <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Initial Stock</th>
                                                                    <th className="px-6 py-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="bg-white divide-y divide-slate-50">
                                                                {generatedVariants.map((v, i) => !v._destroy && (
                                                                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                                                                        <td className="px-6 py-4">
                                                                            <div className="flex flex-wrap gap-1.5">
                                                                                {v.options.map((o: any) => (
                                                                                    <span key={o.id} className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black bg-indigo-50 text-indigo-700 uppercase">{o.presentation}</span>
                                                                                ))}
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-4">
                                                                            <input
                                                                                type="text"
                                                                                value={v.sku}
                                                                                onChange={(e) => updateVariant(i, 'sku', e.target.value)}
                                                                                className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold font-mono outline-none focus:border-indigo-300 transition-all"
                                                                            />
                                                                        </td>
                                                                        <td className="px-6 py-4">
                                                                            <input
                                                                                type="number"
                                                                                value={v.mrp}
                                                                                onChange={(e) => updateVariant(i, 'mrp', e.target.value)}
                                                                                className="w-24 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold font-mono outline-none focus:border-indigo-300 transition-all"
                                                                            />
                                                                        </td>
                                                                        <td className="px-6 py-4">
                                                                            <input
                                                                                type="number"
                                                                                value={v.stock_quantity}
                                                                                onChange={(e) => updateVariant(i, 'stock_quantity', e.target.value)}
                                                                                className="w-20 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-xs font-bold font-mono outline-none focus:border-indigo-300 transition-all"
                                                                            />
                                                                        </td>
                                                                        <td className="px-6 py-4 text-center">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => updateVariant(i, 'active', !v.active)}
                                                                                className={`w-10 h-6 inline-flex items-center rounded-full transition-all focus:outline-none ${v.active ? 'bg-indigo-600' : 'bg-slate-300'}`}
                                                                            >
                                                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all ${v.active ? 'translate-x-5' : 'translate-x-1'}`} />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer Controls */}
                        <div className="bg-slate-50 p-10 border-t border-slate-100 flex items-center justify-between">
                            <div className="flex gap-4">
                                <label className="flex items-center gap-3 cursor-pointer group">
                                    <div className={`w-12 h-6 rounded-full transition-all flex items-center p-1 ${formData.active ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                        <input type="checkbox" name="active" checked={formData.active} onChange={handleChange} className="hidden" />
                                        <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-all ${formData.active ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </div>
                                    <span className="text-sm font-bold text-slate-600 group-hover:text-slate-900 transition-colors">Marketplace Active</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer group border-l border-slate-200 pl-4">
                                    <div className={`w-12 h-6 rounded-full transition-all flex items-center p-1 ${formData.show_in_store ? 'bg-indigo-500' : 'bg-slate-300'}`}>
                                        <input type="checkbox" name="show_in_store" checked={formData.show_in_store} onChange={handleChange} className="hidden" />
                                        <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-all ${formData.show_in_store ? 'translate-x-6' : 'translate-x-0'}`} />
                                    </div>
                                    <span className="text-sm font-bold text-slate-600 group-hover:text-slate-900 transition-colors">Show in Store</span>
                                </label>
                            </div>

                            <div className="flex gap-4">
                                <Link to="/products" className="px-8 py-4 text-sm font-black text-slate-500 hover:text-slate-800 transition-all">Discard Changes</Link>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className={`px-12 py-4 bg-indigo-600 text-white rounded-[1.5rem] text-sm font-black uppercase tracking-[0.2em] shadow-2xl shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-1 active:scale-95 transition-all flex items-center gap-3 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                            Synchronizing...
                                        </>
                                    ) : (
                                        isEdit ? 'Submit Updates' : 'Launch Product'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div >
        </div >
    );
}

export default ProductForm;
